/**
 * PROC-05 Emphasis-prosody density (INV-PACK-05; change C-PROC05-EMPH). Language-agnostic.
 *
 * The pack's composite: the mean of an em-dash sub-score (— per 1k words) and an italic
 * sub-score (emphasis spans per 1k words). Differences from the pack:
 *   - the em-dash sub-score counts ONLY the em-dash U+2014. The pack's regex `—|\s--\s` also counted a spaced
 *     double hyphen; a double hyphen (typed or humaniser-converted) is a different character, so it is reported
 *     as a separate evidence-only feature (`doubleHyphen`, weight 0, its own summary line) that never enters
 *     K, the sub-scores or the composite (operator decision 2026-09-29, C-PROC05-DASH). The "humaniser converts
 *     em-dashes to hyphens" problem is deferred to a per-channel review;
 *   - italics are the PARSED emphasis spans of the document (`doc.spansOfKind('emphasis')`) in prose
 *     paragraphs, bold (`strong`) excluded, instead of a regex over raw markup. A span longer than
 *     80 characters (the pack's `*…*` limit) or containing a line break is not stress emphasis (a
 *     italicised letter, thought or heading) and is not an event, except in a wrapped text (pdf, hard-wrapped txt),
 *     where a newline is a soft wrap and an italic phrase may run over it;
 *   - when `doc.observability.emphasis` is 'none' the italic sub-score abstains and the composite is
 *     the dash sub-score alone (unobservable ≠ absent), with a note;
 *   - each sub-rate is shrunk toward its own human prior (framework fitRate) before its pack
 *     sub-ramp `[[0,0],[8,0],[15,100]]` (dashes) / `[[0,0],[4,0],[12,100]]` (italics) is applied.
 * Display score = mean of the sub-scores (interval: mean of the sub-score intervals).
 *
 * Segments and units: the framework's rate machinery needs ONE rate, so the per-paragraph events are
 * dashes + italics (marks per 1k words) and `def.ramp` is the composite evaluated on the summed rate
 * (thresholds 8+4 = 12 → 0 and 15+12 = 27 → 100). The document score is the exact composite; segment
 * scores are this summed-rate approximation (they agree where both sub-rates are in their linear
 * zone). K/E, φ, κ and the prior are those of the summed rate. Weight 1.5 × 0.5.
 */
import { safeScan } from '../lexicons/regex-list.ts';
import { fmt, proseBlocks, rateDef, wordExposure } from './a-shared.ts';
import { derivePrior, fitRate, overdispersion, rateChannel } from './framework.ts';
import { makeEvidence } from './framework.ts';
import type {
	ChannelDef,
	ChannelImpl,
	ChannelResult,
	Measurement,
	Ramp,
	RunContext,
} from './types.ts';

const DASH_RAMP: Ramp = [
	[0, 0],
	[8, 0],
	[15, 100],
];
const ITALIC_RAMP: Ramp = [
	[0, 0],
	[4, 0],
	[12, 100],
];
const MAX_ITALIC_CHARS = 80;
const DASH = /—/g;
/** A spaced double hyphen: not an em-dash (C-PROC05-DASH); evidence only. */
const DOUBLE_HYPHEN = /\s--\s/g;

export const proc05Def = rateDef({
	id: 'PROC-05',
	name: 'Emphasis-prosody density',
	family: 'PROSODY',
	features: [
		{ id: 'emDash', label: 'Em-dash (U+2014)' },
		{ id: 'italic', label: 'Italic (emphasis) span' },
		{
			id: 'doubleHyphen',
			label: 'Spaced double hyphen " -- " (not an em-dash; evidence only, weight 0)',
		},
	],
	oneWay: true,
	minExposure: 1500,
	exposureUnit: 'words',
	lang: 'agnostic',
	ramp: [
		[0, 0],
		[12, 0],
		[27, 100],
	],
	unitScale: 1000,
	unitLabel: 'emphasis marks (dashes + italics) per 1k words',
	phi0: 1.2,
	weight: 1.5 * 0.5,
	nd: 0.15,
	changes: ['C-PROC05-EMPH', 'C-PROC05-DASH'],
});

const subDef = (ramp: Ramp): ChannelDef => ({
	...proc05Def,
	ramp,
	prior: derivePrior(ramp, 1000, 'words', 1.2),
});
export const dashDef = subDef(DASH_RAMP);
export const italicDef = subDef(ITALIC_RAMP);

export const mean2 = (a: number, b: number): number => (a + b) / 2;

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const P = doc.paragraphs.count;
	const kd = new Float64Array(P);
	const ki = new Float64Array(P);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	let Kd = 0;
	let Ki = 0;
	let dh = 0;
	let words = 0;
	for (const b of proseBlocks(doc)) {
		words += e[b.p] as number;
		if (b.text.includes('—'))
			safeScan(DASH, b.text, b.base, (s, en) => {
				kd[b.p] = (kd[b.p] as number) + 1;
				Kd++;
				ev.add(s, en, 0, 1);
			});
		if (b.text.includes('--'))
			safeScan(DOUBLE_HYPHEN, b.text, b.base, (s, en) => {
				dh++;
				ev.add(s + 1, en - 1, 2, 0);
			});
	}
	const observable = doc.observability.emphasis !== 'none';
	const wrapped = doc.flags.pdfCleaned || doc.flags.hardWrapped;
	if (observable) {
		const pp = doc.paragraphs;
		for (const sp of doc.spansOfKind('emphasis')) {
			const blk = doc.blocks[sp.block];
			if (!blk || blk.register !== 'prose' || blk.kind !== 'paragraph') continue;
			const p = blk.para;
			if (p < 0 || pp.block[p] !== sp.block) continue;
			const len = sp.charEnd - sp.charStart;
			if (len < 1 || len > MAX_ITALIC_CHARS) continue;
			const t = doc.text.slice(sp.charStart, sp.charEnd);
			// a newline in a hard-wrapped text (pdf lines, wrapped txt) is a soft wrap, not a line break of the writer
			if (t.trim() === '' || (t.includes('\n') && !wrapped)) continue;
			ki[p] = (ki[p] as number) + 1;
			Ki++;
			ev.add(sp.charStart, sp.charEnd, 1, 1);
		}
	}
	const k = new Float64Array(P);
	for (let p = 0; p < P; p++) k[p] = (kd[p] as number) + (ki[p] as number);
	const notes: string[] = [];
	const phiDash = overdispersion(kd, e, Kd, words, 1.2);
	const phiItalic = observable ? overdispersion(ki, e, Ki, words, 1.2) : 1.2;
	const fitD = fitRate(dashDef, Kd, words, phiDash);
	const fitI = observable ? fitRate(italicDef, Ki, words, phiItalic) : undefined;
	if (!fitI) notes.push('italic sub-score abstains: emphasis is unobservable in this format');
	const score = fitI ? mean2(fitD.score, fitI.score) : fitD.score;
	const lo = fitI ? mean2(fitD.scoreCi90[0], fitI.scoreCi90[0]) : fitD.scoreCi90[0];
	const hi = fitI ? mean2(fitD.scoreCi90[1], fitI.scoreCi90[1]) : fitD.scoreCi90[1];
	const denom = Math.max(1, words);
	const emK = (Kd / denom) * 1000;
	const itK = (Ki / denom) * 1000;
	return {
		k,
		e,
		evidence: ev,
		notes,
		aux: {
			kDash: kd,
			kItalic: ki,
			phiDash,
			phiItalic,
			observable: observable ? 1 : 0,
		},
		summary: [
			`em-dash ${fmt(emK, 1)}/1k`,
			fitI ? `italic spans ${fmt(itK, 1)}/1k` : 'italic spans unobservable',
			...(dh > 0
				? [`${dh}× spaced double hyphen " -- ": not an em-dash, evidence only (not in the score)`]
				: []),
		],
		features: {
			emDashPerK: emK,
			italicPerK: itK,
			doubleHyphen: dh,
			dashScore: fitD.score,
			...(fitI ? { italicScore: fitI.score } : {}),
			compositeScore: score,
			compositeLo: lo,
			compositeHi: hi,
		},
	};
}

const base = rateChannel(proc05Def, measure);

export const proc05: ChannelImpl = {
	def: proc05Def,
	phase: 'event',
	measure: base.measure,
	run(ctx: RunContext): ChannelResult {
		const r = base.run(ctx);
		if (r.status !== 'scored') return r;
		const f = r.features;
		r.doc.score = f.compositeScore as number;
		r.doc.scoreCi90 = [f.compositeLo as number, f.compositeHi as number];
		return r;
	},
};
export { proc05Def as def, proc05 as impl };
